import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useProfile } from "../hooks/useProfile";
import { useRolodex } from "../hooks/useRolodex";
import { useDiscoveryGraph } from "../hooks/useDiscoveryGraph";
import { useRelationshipNoteDraft } from "../hooks/useRelationshipEditing";
import Nav from "../components/Nav";
import ConnectionEditModal from "../components/ConnectionEditModal";
import ProfileMasthead from "../components/profile/ProfileMasthead";
import ProfileRail from "../components/profile/ProfileRail";
import DetailSection from "../components/profile/DetailSection";
import RelationshipsCard from "../components/profile/RelationshipsCard";
import { SECTIONS } from "../components/profile/sections";
import { contactProfileForEntry } from "../lib/privateContactProfile";
import { publishProfile } from "../lib/profilePublication";
import { entryForContext } from "../lib/facetForm";
import { facetsFor } from "../lib/rolodexContext";
import { removeRolodexFacet, saveRolodexEntry, updateRolodexFacet } from "../lib/rolodexStore";
import { uploadConnectionPhoto, deleteConnectionPhoto } from "../lib/photoStorage";
import { closenessFor, seedDetailsFromPrivateProfile } from "../lib/connectionDetails";
import { normalizePhone } from "../lib/connectionEligibility";

const LIST_KEYS = ["moments", "career", "education", "locations"];

export default function ConnectionProfile() {
  const { connectionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile } = useProfile(user?.uid);
  const { entries, loading, error } = useRolodex(user?.uid);
  const graph = useDiscoveryGraph(user?.uid, entries);

  const [activeContext, setActiveContext] = useState("professional");
  const [showEdit, setShowEdit] = useState(false);
  const [showConfirmRemove, setShowConfirmRemove] = useState(false);
  const [saveError, setSaveError] = useState("");

  const entry = useMemo(
    () => entries.find((item) => item.id === connectionId) || null,
    [entries, connectionId]
  );
  const availableContexts = facetsFor(entry);
  const connection = entryForContext(entry, activeContext, entries);
  const contactProfile = useMemo(
    () => (entry ? contactProfileForEntry(entry) : {}),
    [entry]
  );

  // Entries saved before the detail lists existed still have a role, school,
  // and city on their contact card. Show those as the first row of each list.
  const seeded = useMemo(
    () => (entry ? seedDetailsFromPrivateProfile(entry, contactProfile) : {}),
    [entry, contactProfile]
  );
  const details = useMemo(() => {
    const merged = {};
    LIST_KEYS.forEach((key) => {
      merged[key] = entry?.[key] || seeded[key] || [];
    });
    return merged;
  }, [entry, seeded]);

  const note = useRelationshipNoteDraft({
    sourceKey: entry?.id ? `${entry.id}:${activeContext}` : null,
    value: connection?.private_note || "",
    onSave: entry
      ? (nextNote) => updateRolodexFacet(user?.uid, entry, activeContext, { private_note: nextNote })
      : null,
  });

  useEffect(() => {
    if (!entry) return;
    setActiveContext((current) =>
      entry.facets?.[current]
        ? current
        : entry.facets?.professional
          ? "professional"
          : "personal"
    );
  }, [entry]);

  useEffect(() => {
    setShowConfirmRemove(false);
    setSaveError("");
  }, [connectionId, activeContext]);

  // A deleted or unknown id should not leave the page in limbo.
  useEffect(() => {
    if (!loading && !error && connectionId && entries.length && !entry) {
      navigate("/dashboard", { replace: true });
    }
  }, [loading, error, connectionId, entries.length, entry, navigate]);

  const guard = async (work) => {
    setSaveError("");
    try {
      await work();
    } catch (failure) {
      setSaveError(failure?.message || "That change could not be saved. Try again.");
    }
  };

  /** Persist entry-level detail. Facets are untouched by an omitted key. */
  const saveEntry = (patch) =>
    guard(() => saveRolodexEntry(user?.uid, { ...entry, ...patch, id: entry.id }));

  const saveFacet = (patch) =>
    guard(() => updateRolodexFacet(user?.uid, entry, activeContext, patch));

  const handleUploadPhoto = (file) =>
    guard(async () => {
      const url = await uploadConnectionPhoto(user?.uid, entry.id, file);
      await saveRolodexEntry(user?.uid, { ...entry, photo_url: url, id: entry.id });
    });

  const handleRemovePhoto = () =>
    guard(async () => {
      await deleteConnectionPhoto(user?.uid, entry.id);
      await saveRolodexEntry(user?.uid, { ...entry, photo_url: "", id: entry.id });
    });

  const handleRemoveFacet = () =>
    guard(async () => {
      await removeRolodexFacet(user?.uid, entry, activeContext);
      navigate("/dashboard", { replace: true });
    });

  if (loading) {
    return (
      <>
        <Nav profile={profile} active="connections" />
        <div className="loading-center" style={{ minHeight: "60vh" }}>
          <div className="spinner" />
        </div>
      </>
    );
  }

  if (error || !entry) {
    return (
      <>
        <Nav profile={profile} active="connections" />
        <div className="page-wrapper">
          <div className="page-content">
            <div className="error-banner">
              This connection could not be loaded. <Link to="/dashboard">Back to your rolodex</Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  const name =
    contactProfile.full_name || entry.recipient_name || entry.recipient_phone || "Unknown";
  const photoUrl = entry.photo_url || contactProfile.profile_photo_url || "";
  // Only an email that actually survives their privacy settings is read-only;
  // a hidden one leaves the owner free to record their own.
  const emailIsPublished = Boolean(publishProfile(entry.otherProfile || {})?.email);

  return (
    <>
      <Nav profile={profile} active="connections" />
      <div className="page-wrapper">
        <div className="page-content connection-profile-page">
          <Link to="/dashboard" className="back-link">← Your rolodex</Link>

          {saveError && <div className="error-banner" role="alert">{saveError}</div>}

          <ProfileMasthead
            name={name}
            relationshipType={connection?.relationship_type}
            whereMet={connection?.where_met}
            birthday={entry.birthday}
            availableContexts={availableContexts}
            activeContext={activeContext}
            onContextChange={setActiveContext}
            onChangeWhereMet={(where_met) => saveFacet({ where_met })}
          />

          <div className="profile-layout">
          <ProfileRail
            name={name}
            photoUrl={photoUrl}
            relationshipType={connection?.relationship_type}
            birthday={entry.birthday}
            closeness={closenessFor(entry.facets?.[activeContext])}
            onUploadPhoto={handleUploadPhoto}
            onRemovePhoto={handleRemovePhoto}
            onChangeCloseness={(closeness) => saveFacet({ closeness })}
            contact={{
              email: contactProfile.email || "",
              emailIsPublished,
              phone: entry.recipient_phone || "",
              locations: details.locations,
              socials: entry.socials || {},
              onChangeEmail: (email) =>
                saveEntry({ private_profile: { ...(entry.private_profile || {}), email } }),
              onChangePhone: (phone) => saveEntry({ recipient_phone: normalizePhone(phone) }),
              onChangeBirthday: (birthday) => saveEntry({ birthday }),
              onChangeLocations: (locations) => saveEntry({ locations }),
              onChangeSocials: (socials) => saveEntry({ socials }),
            }}
          />

          <div className="profile-dossier">
            <section className="dossier-section note-section">
              <div className="dossier-head">
                <h2>Private note</h2>
                <span className="dossier-badge">Only you</span>
              </div>
              <textarea
                className="note-field"
                value={note.value}
                onChange={(event) => note.setValue(event.target.value)}
                onBlur={note.save}
                aria-label={`Private note about ${name}`}
                aria-busy={note.isSaving}
                disabled={note.isSaving}
                placeholder="What should future you remember about this person?"
              />
              {note.status === "saving" && <p className="note-status" role="status">Saving…</p>}
              {note.status === "saved" && <p className="note-status" role="status">Saved</p>}
              {note.status === "error" && <p className="error-banner" role="alert">{note.error}</p>}
            </section>

            <DetailSection
              {...SECTIONS.moments}
              items={details.moments}
              onChange={(moments) => saveEntry({ moments })}
            />

            <DetailSection
              {...SECTIONS.career}
              items={details.career}
              onChange={(career) => saveEntry({ career })}
            />

            <DetailSection
              {...SECTIONS.education}
              items={details.education}
              onChange={(education) => saveEntry({ education })}
            />

            <DetailSection
              {...SECTIONS.locations}
              items={details.locations}
              onChange={(locations) => saveEntry({ locations })}
            />

            <RelationshipsCard
              entry={entry}
              connection={connection}
              entries={entries}
              graph={graph}
              onEditOrigin={() => setShowEdit(true)}
            />
          </div>

          </div>

          <div className="profile-page-actions">
            <button className="btn-ghost btn-sm" onClick={() => setShowEdit(true)}>
              Edit relationship details
            </button>
            {showConfirmRemove ? (
              <div className="profile-page-confirm">
                <button className="btn-danger-ghost btn-sm" onClick={handleRemoveFacet}>
                  {availableContexts.length > 1
                    ? `Confirm remove ${activeContext}`
                    : "Confirm remove"}
                </button>
                <button className="btn-ghost btn-sm" onClick={() => setShowConfirmRemove(false)}>
                  Cancel
                </button>
              </div>
            ) : (
              <button
                className="btn-danger-ghost btn-sm"
                onClick={() => setShowConfirmRemove(true)}
              >
                {availableContexts.length > 1 ? `Remove ${activeContext}` : "Remove connection"}
              </button>
            )}
          </div>
        </div>
      </div>

      {showEdit && (
        <ConnectionEditModal
          entryId={entry.id}
          entries={entries}
          currentUser={user}
          initialContext={activeContext}
          networkNodes={graph.nodes}
          onClose={() => setShowEdit(false)}
        />
      )}
    </>
  );
}
